import { HamburgerIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Show,
} from '@chakra-ui/react';
import type { IconType } from 'react-icons';
import {
  FiBookOpen,
  FiInfo,
  FiLogIn,
  FiMail,
  FiTrendingUp,
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

import { APP_URL, SIGNUP_URL } from '../config/api';
import { buildSignupUrl, trackSignupClick } from '../utils/analytics';

const DISCORD_URL = 'https://discord.com/invite/MUpH2jqg94';

const NAV_LINKS: { label: string; to: string; icon: IconType }[] = [
  { label: 'Ratings', to: '/ratings', icon: FiTrendingUp },
  { label: 'About Us', to: '/about', icon: FiInfo },
  { label: 'Contact', to: '/contact', icon: FiMail },
  { label: 'Blog', to: '/blog', icon: FiBookOpen },
];

const ghostNavProps = {
  variant: 'ghost' as const,
  size: { base: 'md', md: 'lg' } as const,
  px: { base: 3, md: 4 },
  color: 'gray.200',
  fontWeight: 'bold' as const,
  fontSize: { base: 'md', md: 'lg' },
  _hover: { textDecoration: 'none', bg: 'gray.800', color: 'white' },
};

const menuItemProps = {
  bg: 'gray.900',
  color: 'gray.200',
  fontWeight: 'bold' as const,
  iconSpacing: 3,
  _hover: { bg: 'gray.800', color: 'white' },
  _focus: { bg: 'gray.800', color: 'white' },
};

function DiscordIcon() {
  return (
    <Icon viewBox="0 0 24 24" boxSize={4}>
      <path
        fill="currentColor"
        d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
      />
    </Icon>
  );
}

type NavbarProps = {
  /** When true, rendered without the header shell (e.g. embedded layouts). */
  embedded?: boolean;
};

export default function Navbar({ embedded = false }: NavbarProps) {
  const inner = (
    <Container maxW="container.xl" px={{ base: 3, md: 4 }}>
      <Flex
        h={{ base: '72px', md: '80px' }}
        align="center"
        justify="space-between"
        gap={2}
      >
        <HStack
          spacing={3}
          as={RouterLink}
          to="/"
          flex="1"
          minW={0}
          mr={{ base: 1, md: 0 }}
        >
          <Image
            src="/assets/images/ww_logo_dark.svg"
            alt="WagerWatchdog"
            h={{ base: '52px', md: '48px' }}
            w="auto"
            maxW={{ base: 'min(220px, 58vw)', md: 'none' }}
            objectFit="contain"
          />
        </HStack>
        <HStack spacing={{ base: 2, md: 4 }} flexShrink={0}>
          <Show above="md">
            {NAV_LINKS.map((item) => (
              <Button
                key={item.to}
                as={RouterLink}
                to={item.to}
                {...ghostNavProps}
              >
                {item.label}
              </Button>
            ))}
            <Button
              as="a"
              href={`${APP_URL}/login`}
              rel="noopener noreferrer"
              {...ghostNavProps}
              px={{ base: 3, md: 5 }}
            >
              Log In
            </Button>
          </Show>

          <Button
            as="a"
            href={buildSignupUrl(SIGNUP_URL)}
            rel="noopener noreferrer"
            variant="primary"
            size={{ base: 'md', md: 'lg' }}
            px={{ base: 4, md: 6 }}
            fontWeight="bold"
            fontSize={{ base: 'md', md: 'lg' }}
            _hover={{ textDecoration: 'none' }}
            onClick={() => trackSignupClick('navbar')}
          >
            Sign Up
          </Button>

          <Show below="md">
            <IconButton
              as={Link}
              href={DISCORD_URL}
              aria-label="Join Discord"
              icon={<DiscordIcon />}
              size="sm"
              minW="36px"
              target="_blank"
              rel="noopener noreferrer"
              bg="#5865F2"
              color="white"
              _hover={{
                textDecoration: 'none',
                bg: '#4752C4',
              }}
            />
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                aria-label="Open page menu"
                icon={<HamburgerIcon boxSize={4} />}
                variant="ghost"
                size="sm"
                minW="36px"
                color="gray.200"
                _hover={{ bg: 'gray.800', color: 'white' }}
                _active={{ bg: 'gray.800', color: 'white' }}
              />
              <MenuList
                bg="gray.900"
                borderColor="whiteAlpha.200"
                py={2}
                minW="180px"
              >
                {NAV_LINKS.map((item) => (
                  <MenuItem
                    key={item.to}
                    as={RouterLink}
                    to={item.to}
                    icon={<Icon as={item.icon} boxSize={4} />}
                    {...menuItemProps}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem
                  as="a"
                  href={`${APP_URL}/login`}
                  rel="noopener noreferrer"
                  icon={<Icon as={FiLogIn} boxSize={4} />}
                  {...menuItemProps}
                >
                  Log In
                </MenuItem>
              </MenuList>
            </Menu>
          </Show>

          <Show above="md">
            <Button
              as={Link}
              href={DISCORD_URL}
              size="lg"
              leftIcon={<DiscordIcon />}
              target="_blank"
              rel="noopener noreferrer"
              bg="#5865F2"
              color="white"
              fontWeight="bold"
              fontSize="lg"
              borderRadius="8px"
              _hover={{
                textDecoration: 'none',
                bg: '#4752C4',
                transform: 'translateY(-1px)',
                boxShadow: 'md',
              }}
              _active={{ transform: 'translateY(0)' }}
            >
              Join Discord
            </Button>
          </Show>
        </HStack>
      </Flex>
    </Container>
  );

  if (embedded) {
    return inner;
  }

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={100}
      bg="rgba(17, 24, 39, 0.95)"
      backdropFilter="blur(12px)"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
    >
      {inner}
    </Box>
  );
}
